import { reactive, ref, computed } from 'vue'
import { cloneDeep } from 'lodash-es'
import { processCodes } from '@/lib/dataProcessor/dataHandlers'
import { codePaths, codeIs } from '@/lib/codes'
import { getCodeIdByPath } from '../codesUtils'
import { REFERENCES } from '@/lib/references'
import { apiClientV2, type ApiError } from '@/ContextTab/services/apiClient'
import type { CodeWithCategoryAndChildren } from '@/api/generated/v2'


type Profiles = Record<ProfileTypeName, CodeWithCategoryAndChildren[] | null>
type ProfileType = keyof typeof REFERENCES['Profile']['Type']
export type ProfileTypeName = typeof REFERENCES['Profile']['Type'][ProfileType]['name']

function getProfileCodeItems(
  profiles: Profiles,
  profileName: ProfileTypeName,
  idField: string,
  // аргумент для формирования списка кодов профиля, собранных в категорию профиля
  profileCategory: any = null,
) {
  const profileCodes = cloneDeep(profiles[profileName] ?? null)

  if (!profileCodes) {
    return null
  }

  const codes = profileCategory
    ? processCodes([Object.assign(profileCategory, { children: [...profileCodes] })], idField)
    : processCodes(profileCodes, idField)

  const rootCodeId = getCodeIdByPath(codes, codePaths.allCodes)

  if (rootCodeId !== null) {
    if (codes[rootCodeId]) {
      delete codes[rootCodeId]
    } else {
      delete codes['-1']
    }
  } else {
    delete codes['-1']
  }
  return codes
}

export function useCodeProfiles() {
  const profiles = reactive<Profiles>({
    PUBLISH_NEWS: null,
    NEWS_OR_ISSUE: null,
    PUBLISH_ISSUE: null,
    DEFAULT: null,
  })

  const errors = ref<Record<ProfileTypeName, ApiError[]> | Record<string, never>>({})

  const newsCodesById = computed(() =>
    getProfileCodeItems(profiles, 'NEWS_OR_ISSUE', 'id'),
  )

  const publicationCodesById = computed(() =>
    getProfileCodeItems(profiles, 'PUBLISH_NEWS', 'id'),
  )

  const newsCodesByUid = computed(() =>
    getProfileCodeItems(profiles, 'NEWS_OR_ISSUE', 'uid'),
  )

  const publicationCodesByUid = computed(() =>
    getProfileCodeItems(profiles, 'PUBLISH_NEWS', 'uid'),
  )

  const newsCodesByIdWithProfileCategory = computed(() =>
    getProfileCodeItems(profiles, 'NEWS_OR_ISSUE', 'id', {
      children: [],
      name: 'Не публикующие коды',
    }),
  )

  const publicationCodesByIdWithProfileCategory = computed(() =>
    getProfileCodeItems(profiles, 'PUBLISH_NEWS', 'id', {
      children: [],
      name: 'Публикующие коды',
    }),
  )

  const isUserHasPublishingCodes = computed(() => {
    if (publicationCodesById.value) {
      const pubCodes = { ...publicationCodesById.value }

      const codes: any[] = Object.values(pubCodes)

      const hasNonTechnicalCodes = codes.some((code) => {
        // технологический код (сюда попадает сама категория и все ее дети)
        if (codeIs.publicationTechnical(code)) {
          return false
        }

        /**
         * игнорируем категории, т.к. в АСОИ можно добавить в профиль категорию без ее детей.
         * то есть наличие категории не гарантирует, что у юзера есть коды из этой категории
         * UPD: ничто другое этого тоже не гарантирует на 23.12.2024
         */
        if (code?.children?.length) {
          return false
        }

        return true
      })
      return pubCodes && hasNonTechnicalCodes
    }
    return false
  })

  async function fetchCodeProfile(profileId: ProfileType) {
    const profileName: ProfileTypeName | undefined = REFERENCES.Profile.Type[profileId].name

    if (!profileName) {
      console.warn('fetchCodeProfile cannot continue because references did not load')
      return
    }

    errors[profileName] = null
    const response = await apiClientV2.CodesService.profileCodesCodesProfileGet({
      query: {
        type_id: profileId,
      },
    })

    if (response.error) {
      errors[profileName] = response.error.errors
      profiles[profileName] = null
      return null
    } else {
      profiles[profileName] = response.data
      return response
    }
  }

  return {
    newsCodesById,
    publicationCodesById,

    newsCodesByUid,
    publicationCodesByUid,

    newsCodesByIdWithProfileCategory,
    publicationCodesByIdWithProfileCategory,

    isUserHasPublishingCodes,

    profiles,
    errors,

    fetchCodeProfile,
  }
}
